/**
 * IndicatorRow — port of the ind-row block in layout.py.
 * Two columns of inline checkboxes: OVERLAYS and SUBPANELS.
 * Defaults match the Python p-overlays / p-panels initial values.
 */
import { useStore } from '@/store';
import type { OverlayId, SubpanelId } from '@/types';
import { ORG } from '@/theme';

const OVERLAYS: Array<[OverlayId, string]> = [
  ['sma20',  'SMA 20'],
  ['sma50',  'SMA 50'],
  ['sma200', 'SMA 200'],
  ['bb',     'Bollinger Bands'],
  ['don',    'Adaptive Donchian'],
  ['vwap',   'VWAP'],
  ['piv',    'Pivot Points'],
  ['owalls', 'Options Walls'],
  ['vp',     'Volume Profile (POC/VAH/VAL)'],
];

const PANELS: Array<[SubpanelId, string]> = [
  ['vol',   'Volume'],
  ['rsi',   'RSI'],
  ['macd',  'MACD'],
  ['atr',   'ATR'],
  ['obv',   'OBV'],
  ['stoch', 'Stochastic'],
];

function Check<T extends string>(
  { value, label, on, onToggle }:
  { value: T; label: string; on: boolean; onToggle: (v: T) => void },
) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      marginRight: 10, color: '#e0e0e0', fontSize: 10,
      fontFamily: "Consolas, monospace", cursor: 'pointer',
    }}>
      <input
        type="checkbox"
        checked={on}
        onChange={() => onToggle(value)}
        style={{ accentColor: ORG, marginRight: 3 }}
      />
      {label}
    </label>
  );
}

const VP_SESSIONS: Array<1 | 2 | 5 | 10> = [1, 2, 5, 10];

export function IndicatorRow() {
  const overlays    = useStore(s => s.overlays);
  const panels      = useStore(s => s.panels);
  const vpSessions  = useStore(s => s.vpSessions);
  const togO        = useStore(s => s.toggleOverlay);
  const togP        = useStore(s => s.togglePanel);
  const setVpN      = useStore(s => s.setVpSessions);

  const vpOn = overlays.includes('vp');

  return (
    <div className="ind-row">
      <div className="ind-col">
        <div className="ind-label">OVERLAYS</div>
        <div>
          {OVERLAYS.map(([v, lbl]) => (
            <Check key={v} value={v} label={lbl} on={overlays.includes(v)} onToggle={togO} />
          ))}
        </div>
        {vpOn && (
          <div style={{
            display: 'flex', gap: 4, alignItems: 'center',
            paddingLeft: 4, marginTop: 2,
            color: '#888', fontSize: 9, fontFamily: 'Consolas, monospace',
          }}>
            <span style={{ letterSpacing: 1 }}>VP SESSIONS:</span>
            {VP_SESSIONS.map(n => (
              <button key={n} onClick={() => setVpN(n)} style={{
                background: vpSessions === n ? 'var(--accent)' : 'transparent',
                color:      vpSessions === n ? 'var(--accent-ink)' : '#888',
                border:     '1px solid ' + (vpSessions === n ? 'var(--accent)' : '#333'),
                fontFamily: 'Consolas, monospace', fontSize: 9,
                padding: '1px 6px', borderRadius: 2, cursor: 'pointer',
              }}>{n}</button>
            ))}
          </div>
        )}
      </div>
      <div className="ind-col">
        <div className="ind-label">SUBPANELS  (all render simultaneously)</div>
        <div>
          {PANELS.map(([v, lbl]) => (
            <Check key={v} value={v} label={lbl} on={panels.includes(v)} onToggle={togP} />
          ))}
        </div>
      </div>
    </div>
  );
}
